import React from 'react';
import {
  formatCurrency,
  ifNullOrWhitespace,
  isArray,
  isFunc,
  PartTypes,
} from 'vdlib';
import LookupsHelper from 'lib/LookupsHelper';

const defaultAmountDataProperty = 'Amount';
const defaultReferenceAmountDataProperty = 'PartPrice';
const defaultBidsDataProperty = 'Bids';
const maxDiscountPercent = 30;

const noDiffClassName = 'red-mint';
const noAmountClassName = 'grey-silver';
const percentileClassNames = [
  'yellow-gold',
  'yellow-crusta',
  'yellow-soft',
  'green-dark',
  'green-meadow',
  'green-jungle',
];

const renderDiff = (referenceAmount, amount, withMargin) => {
  let diff = referenceAmount - amount,
    className;
  if (referenceAmount <= 0) {
    className = noAmountClassName;
  } else if (diff <= 0) {
    className = noDiffClassName;
  } else {
    let maxDiff = (referenceAmount * maxDiscountPercent) / 100,
      percentile = Math.floor((diff * percentileClassNames.length) / maxDiff);
    className =
      percentileClassNames[
        percentile >= percentileClassNames.length
          ? percentileClassNames.length - 1
          : percentile
      ];
  }

  diff -= diff * 2;
  return (
    <span
      className={`lowest-bid-diff ${
        withMargin === true ? 'ml-sm' : ''
      } font-${className}`}
    >
      {formatCurrency(diff)}
    </span>
  );
};

export const getAmountDiffCellRenderer = (
  amountDataProperty,
  partPriceDataProperty
) => {
  return ({ data }) => (
    <div>
      {renderDiff(
        data[
          ifNullOrWhitespace(
            partPriceDataProperty,
            defaultReferenceAmountDataProperty
          )
        ],
        data[ifNullOrWhitespace(amountDataProperty, defaultAmountDataProperty)],
        true
      )}
    </div>
  );
};

export const getStaticAmountDiffCellRenderer = (
  referenceAmount,
  amountDataProperty,
  showOnlyDiff
) => {
  return (_, data) => (
    <div>
      {showOnlyDiff === true ? false : formatCurrency(referenceAmount)}
      {renderDiff(
        referenceAmount,
        data[ifNullOrWhitespace(amountDataProperty, defaultAmountDataProperty)],
        true
      )}
    </div>
  );
};

const defaultTemplate = <div className="no-bid"> - </div>,
  smallLabelStyle = {
    float: 'none',
    height: '10px',
    marginRight: '2px',
    display: 'inline-block',
  };

export const getLowestBidAmountDiffCellRenderer = (
  visiblePartTypes,
  bids,
  partPrice
) => {
  let visiblePartTypesGetter = () => visiblePartTypes;
  // let bids = data[bidsDataProperty] || data[defaultBidsDataProperty] || [];
  if (isFunc(visiblePartTypesGetter)) {
    let visiblePartTypes = visiblePartTypesGetter().map(partType =>
      LookupsHelper.getIdByDescription('partTypes', partType)
    );

    if (visiblePartTypes.length == 0) return defaultTemplate;

    bids = bids
      ? bids.filter(b => visiblePartTypes.indexOf(b.PartTypeID) >= 0)
      : [];
  }

  if (bids.length == 0) return defaultTemplate;

  let lowestBid = bids[0];
  for (let bid of bids) if (bid.Amount < lowestBid.Amount) lowestBid = bid;

  let labelCassName =
    lowestBid.PartTypeID ==
    LookupsHelper.getIdByDescription('partTypes', PartTypes.New)
      ? 'success'
      : lowestBid.PartTypeID ==
        LookupsHelper.getIdByDescription('partTypes', PartTypes.Used)
      ? 'danger'
      : 'warning';
  return (
    <div>
      <div className="lowest-bid">
        <span
          className={`label label-${labelCassName}`}
          style={smallLabelStyle}
        />
        {formatCurrency(lowestBid.Amount)}
      </div>
      {renderDiff(partPrice, lowestBid.Amount)}
    </div>
  );
};

export const getSelectedCellRenderer = (visiblePartTypes, bids) => {
  let visiblePartTypesGetter = () => visiblePartTypes;

  if (isFunc(visiblePartTypesGetter)) {
    let visiblePartTypes = visiblePartTypesGetter().map(partType =>
      LookupsHelper.getIdByDescription('partTypes', partType)
    );

    if (visiblePartTypes.length == 0) return defaultTemplate;

    bids = bids
      ? bids.filter(b => visiblePartTypes.indexOf(b.PartTypeID) >= 0)
      : [];
  }

  if (bids.length == 0) return defaultTemplate;

  let selectedBid = bids.find(x => x.IsSelected);

  if (!selectedBid) return defaultTemplate;

  let labelCassName =
    selectedBid.PartTypeID ==
    LookupsHelper.getIdByDescription('partTypes', PartTypes.New)
      ? 'success'
      : selectedBid.PartTypeID ==
        LookupsHelper.getIdByDescription('partTypes', PartTypes.Used)
      ? 'danger'
      : 'warning';
  return (
    <div>
      <div className="lowest-bid">
        <span
          className={`label label-${labelCassName}`}
          style={smallLabelStyle}
        />
        {formatCurrency(selectedBid.Amount)}
      </div>
    </div>
  );
};

export const getMerchantLowestBidAmount = (user, bidsDataProperty) => {
  return (_, data) => {
    let bids = data[bidsDataProperty];
    if (!isArray(bids)) bids = data[defaultBidsDataProperty];
    if (!isArray(bids) || bids.length === 0)
      return <div className="no-bid"> - </div>;

    let lowestBid = bids[0];
    for (let bid of bids)
      if (bid.Amount < lowestBid.Amount && bid.BidderID === user.PartyId)
        lowestBid = bid;
    return <div>{formatCurrency(lowestBid.Amount)}</div>;
  };
};
